import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import Navbar from './Navbar';
import * as FiIcons from 'react-icons/fi';

const { FiMonitor, FiImage, FiPlay, FiUsers, FiTrendingUp, FiPlus, FiActivity, FiGrid } = FiIcons;

const Dashboard = () => {
  const { user } = useAuth();
  const [stats] = useState({
    totalMonitors: 12,
    activeMonitors: 10,
    totalMedia: 45,
    totalViews: 1250
  });

  const recentActivity = [
    {
      id: 1,
      action: 'Monitor "Lobby Display" went online',
      time: '2 minutes ago',
      type: 'online'
    },
    {
      id: 2,
      action: 'New media "Summer Promo" uploaded',
      time: '15 minutes ago',
      type: 'upload'
    },
    {
      id: 3,
      action: 'Playlist "Store Front" updated',
      time: '1 hour ago',
      type: 'update'
    },
    {
      id: 4,
      action: 'Monitor "Cafe Screen" went offline',
      time: '2 hours ago',
      type: 'offline'
    }
  ];

  const quickActions = [
    {
      icon: FiMonitor,
      title: 'Add Monitor',
      description: 'Connect a new display',
      link: '/monitors',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: FiImage,
      title: 'Upload Media',
      description: 'Add new content',
      link: '/media',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: FiGrid,
      title: 'Multi-Screen Sync',
      description: 'Create video walls',
      link: '/sync-groups',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: FiUsers,
      title: 'Manage Users',
      description: 'Team collaboration',
      link: '/dashboard',
      color: 'from-orange-500 to-red-500'
    }
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="pt-20 px-4 pb-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <h1 className="text-3xl font-bold text-white mb-2">
              Welcome back, {user?.name}!
            </h1>
            <p className="text-white/70">
              Here's what's happening with your digital signage network
            </p>
          </motion.div>

          {/* Stats Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          >
            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiMonitor} className="text-white text-xl" />
                </div>
                <span className="text-2xl font-bold text-white">{stats.totalMonitors}</span>
              </div>
              <h3 className="text-white/80 font-medium">Total Monitors</h3>
              <p className="text-green-400 text-sm mt-1">+2 this month</p>
            </div>
            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-400 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiActivity} className="text-white text-xl" />
                </div>
                <span className="text-2xl font-bold text-white">{stats.activeMonitors}</span>
              </div>
              <h3 className="text-white/80 font-medium">Active Monitors</h3>
              <p className="text-green-400 text-sm mt-1">83% uptime</p>
            </div>
            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiImage} className="text-white text-xl" />
                </div>
                <span className="text-2xl font-bold text-white">{stats.totalMedia}</span>
              </div>
              <h3 className="text-white/80 font-medium">Media Files</h3>
              <p className="text-green-400 text-sm mt-1">+5 this week</p>
            </div>
            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-red-400 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiTrendingUp} className="text-white text-xl" />
                </div>
                <span className="text-2xl font-bold text-white">{stats.totalViews.toLocaleString()}</span>
              </div>
              <h3 className="text-white/80 font-medium">Total Views</h3>
              <p className="text-green-400 text-sm mt-1">+12% vs last month</p>
            </div>
          </motion.div>

          {/* New Feature Promotion */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-lg border border-white/20 rounded-2xl p-6 mb-8"
          >
            <div className="flex flex-col md:flex-row items-center">
              <div className="md:w-2/3 mb-6 md:mb-0 md:pr-6">
                <div className="flex items-center mb-2">
                  <div className="bg-purple-500/30 text-purple-300 text-xs font-semibold px-2 py-1 rounded-full mr-2">NEW</div>
                  <h3 className="text-white font-semibold">Multi-Screen Synchronization</h3>
                </div>
                <p className="text-white/70 mb-4">
                  Create stunning video walls by synchronizing content across multiple displays. Perfect for retail storefronts, lobbies, and event venues.
                </p>
                <Link 
                  to="/sync-groups" 
                  className="inline-flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg text-white hover:bg-white/20 transition-all duration-300"
                >
                  <SafeIcon icon={FiGrid} />
                  <span>Create Sync Group</span>
                </Link>
              </div>
              <div className="md:w-1/3 bg-white/5 rounded-xl p-4 flex items-center justify-center">
                <div className="grid grid-cols-3 gap-1">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="w-12 h-20 bg-purple-500/20 border border-purple-400/30 rounded-lg flex items-center justify-center">
                      <span className="text-white/70 text-sm">{i}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-2"
            >
              <h2 className="text-xl font-semibold text-white mb-6">Quick Actions</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {quickActions.map((action, index) => (
                  <Link
                    key={index}
                    to={action.link}
                    className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6 hover:bg-white/15 transition-all duration-300 group"
                  >
                    <div
                      className={`w-12 h-12 bg-gradient-to-br ${action.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}
                    >
                      <SafeIcon icon={action.icon} className="text-white text-xl" />
                    </div>
                    <h3 className="text-white font-semibold mb-2">{action.title}</h3>
                    <p className="text-white/70 text-sm">{action.description}</p>
                  </Link>
                ))}
              </div>
            </motion.div>

            {/* Recent Activity */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="lg:col-span-1"
            >
              <h2 className="text-xl font-semibold text-white mb-6">Recent Activity</h2>
              <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
                <div className="space-y-4">
                  {recentActivity.map((activity) => (
                    <div key={activity.id} className="flex items-start space-x-3">
                      <div
                        className={`w-2 h-2 rounded-full mt-2 ${
                          activity.type === 'online'
                            ? 'bg-green-400'
                            : activity.type === 'offline'
                            ? 'bg-red-400'
                            : activity.type === 'upload'
                            ? 'bg-blue-400'
                            : 'bg-yellow-400'
                        }`}
                      ></div>
                      <div className="flex-1">
                        <p className="text-white/90 text-sm">{activity.action}</p>
                        <p className="text-white/50 text-xs mt-1">{activity.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;